import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'
import { Loader2 } from 'lucide-react'
import { lazy, Suspense } from 'react'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { CampaignProvider } from '@/contexts/CampaignContext'
import { ClientProvider } from '@/contexts/ClientContext'
import AppLayout from '@/layouts/AppLayout'
// Login fica estático pra zero overhead no first paint pre-auth
import Login from '@/pages/Login'
import ErrorBoundary from '@/components/ErrorBoundary'
import EnvBadge from '@/components/EnvBadge'
import CookieBanner from '@/components/CookieBanner'
import type { ReactNode } from 'react'

// A19: code-splitting por rota. Cada page vira chunk próprio (~50-200KB cada
// vs main bundle ~2.7MB tudo junto). Usuário só baixa página em que navegar.
// Login fica estático (acima) — primeira tela pre-auth, sem overhead aceitável.
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const Campanhas = lazy(() => import('@/pages/Campanhas'))
const Influenciadores = lazy(() => import('@/pages/Influenciadores'))
const Cronograma = lazy(() => import('@/pages/Cronograma'))
const Relatórios = lazy(() => import('@/pages/Relatorios'))
const PainelPerformance = lazy(() => import('@/pages/PainelPerformance'))
const DetalheEntrega = lazy(() => import('@/pages/DetalheEntrega'))
const Equipe = lazy(() => import('@/pages/Equipe'))
const Configurações = lazy(() => import('@/pages/Configuracoes'))
const ConfiguraçõesAgência = lazy(() => import('@/pages/ConfiguracoesAgencia'))
const Aprovações = lazy(() => import('@/pages/Aprovacoes'))
const Pagamentos = lazy(() => import('@/pages/Pagamentos'))
const Clientes = lazy(() => import('@/pages/Clientes'))
const Curadoria = lazy(() => import('@/pages/Curadoria'))
const PortalInfluenciador = lazy(() => import('@/pages/PortalInfluenciador'))
const SocialConnect = lazy(() => import('@/pages/SocialConnect'))
const PoliticaPrivacidade = lazy(() => import('@/pages/PoliticaPrivacidade'))
const TermosUso = lazy(() => import('@/pages/TermosUso'))
const PlatformAdminLayout = lazy(() => import('@/pages/admin/PlatformAdminLayout'))
const PlatformAdminTenants = lazy(() => import('@/pages/admin/PlatformAdminTenants'))
const PlatformAdminUsers = lazy(() => import('@/pages/admin/PlatformAdminUsers'))

// Fallback enquanto chunk carrega — mesma UI dos loading states do app
function RouteLoading() {
  return (
    <div className="min-h-screen bg-bg flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-primary animate-spin" />
    </div>
  )
}

/**
 * Detecta se estamos no subdomain admin.runnaflow.com.
 * Também retorna true em preview/dev se VITE_ADMIN_HOST=true ou hostname
 * começa com 'admin.' — útil pra testar o admin layout em preview URLs.
 */
function isAdminHost(): boolean {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname
  if (host === 'admin.runnaflow.com') return true
  if (host === 'admin-dev.runnaflow.com') return true
  if (host.startsWith('admin.') || host.startsWith('admin-')) return true
  if (import.meta.env.VITE_ADMIN_HOST === 'true') return true
  return false
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading, access, signOut, isPlatformAdmin, impersonating } = useAuth()
  const location = useLocation()

  if (loading || access.state === 'loading') {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  // Platform admins no app principal só entram via god mode marker na
  // sessionStorage (setado pelo ?tenant=X no primeiro load após "Entrar" do
  // admin panel). Se não tem marker, redireciona pro painel — evita
  // platform admin cair aleatoriamente num tenant sem ter escolhido.
  // Durante impersonação, libera (impersonation tem fluxo próprio).
  if (isPlatformAdmin && !impersonating && typeof window !== 'undefined') {
    const godMode = sessionStorage.getItem('runna_god_mode_active')
    if (!godMode) {
      const host = window.location.hostname
      const adminOrigin =
        host === 'runnaflow.com' || host === 'www.runnaflow.com'
          ? 'https://admin.runnaflow.com'
        : host === 'runnaflow-dev.runnaflow.com'
          ? 'https://admin-dev.runnaflow.com'
        : null
      if (adminOrigin) {
        window.location.replace(`${adminOrigin}/tenants`)
        return (
          <div className="min-h-screen bg-bg flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          </div>
        )
      }
    }
  }

  if (access.state === 'denied') {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-6">
        <div className="bg-card border border-card-border rounded-2xl max-w-md w-full p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center"
               style={{ background: 'var(--color-danger-soft)', color: 'var(--color-danger)' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <h1 className="font-heading text-xl font-bold text-bright mb-2">Sem acesso</h1>
          <p className="text-sm text-dim leading-relaxed mb-6">{access.reason}</p>
          <button
            onClick={() => signOut()}
            className="px-4 py-2 rounded-md bg-primary text-white text-sm font-semibold cursor-pointer hover:brightness-110"
          >
            Sair e tentar outro email
          </button>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

/** Gate extra pra rotas admin (/platform-admin em main app OU subdomínio admin.*) */
function PlatformAdminGate({ children }: { children: ReactNode }) {
  const { realProfile, user, loading, access, signOut, impersonating, endImpersonation } = useAuth()
  const hostIsAdminDomain = typeof window !== 'undefined' && isAdminHost()

  if (loading || access.state === 'loading') {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    )
  }

  if (!user) return <Navigate to="/login" replace />

  // Impersonando: realProfile finge is_platform_admin=true pra UI mostrar
  // identidade do admin, mas o JWT na requisição é do target — qualquer RPC
  // platform admin retornaria "unauthorized". Bloquear acesso à área admin
  // enquanto impersonando pra evitar UX quebrada (listas vazias + toasts).
  // Em admin.* ainda precisa renderizar em vez de redirect (evita loop).
  if (impersonating) {
    if (hostIsAdminDomain) {
      return (
        <div className="min-h-screen bg-bg flex items-center justify-center p-6">
          <div className="bg-card border border-card-border rounded-2xl max-w-md w-full p-8 text-center">
            <h1 className="font-heading text-xl font-bold text-bright mb-2">Impersonando</h1>
            <p className="text-sm text-dim leading-relaxed mb-6">
              Encerre a impersonação de <strong>{impersonating.targetName}</strong> pra voltar ao painel Platform Admin.
            </p>
            <button
              onClick={() => endImpersonation()}
              className="px-4 py-2 rounded-md bg-primary text-white text-sm font-semibold cursor-pointer hover:brightness-110"
            >
              Encerrar impersonação
            </button>
          </div>
        </div>
      )
    }
    return <Navigate to="/" replace />
  }

  if (!realProfile?.is_platform_admin) {
    // Estado órfão em admin.*: usuário não-platform-admin logado no subdomínio
    // admin (ex: impersonação abortada no meio deixou session target sem stash
    // de retorno). Auto-limpar + redirect pra /login em vez de pedir pro user
    // clicar "Sair". Se for admin real tentando outro email, ele loga de novo.
    if (hostIsAdminDomain) {
      // Fire-and-forget: limpa tudo e manda pra login. Usa flag no sessionStorage
      // pra evitar loop caso signOut demore e rerender dispare de novo.
      if (!sessionStorage.getItem('runna_orphan_cleanup')) {
        sessionStorage.setItem('runna_orphan_cleanup', '1')
        signOut().finally(() => {
          try { localStorage.clear() } catch { /* ignore */ }
          // eslint-disable-next-line react-hooks/immutability -- side-effect intencional fora do render (callback async pós-signOut)
          window.location.href = '/login'
        })
      }
      return (
        <div className="min-h-screen bg-bg flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
      )
    }
    return <Navigate to="/" replace />
  }
  // Chegou aqui → acesso válido. Limpa flag de cleanup.
  if (typeof window !== 'undefined') sessionStorage.removeItem('runna_orphan_cleanup')

  return <>{children}</>
}

function AppRoutes() {
  return (
    <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route path="/portal/:token" element={<PortalInfluenciador />} />
        <Route path="/connect/:token" element={<SocialConnect />} />
        <Route path="/login" element={<Login />} />

        {/* Páginas legais públicas (LGPD) — sem auth */}
        <Route path="/privacidade" element={<PoliticaPrivacidade />} />
        <Route path="/termos" element={<TermosUso />} />

        {/* Platform admin routes — only accessible to allowlist emails.
            Rendered on admin.runnaflow.com via vercel.json rewrite, or on
            runnaflow.com via direct URL (also gated). User comum nunca passa. */}
        <Route
          path="/platform-admin"
          element={<PlatformAdminGate><PlatformAdminLayout /></PlatformAdminGate>}
        >
          <Route index element={<Navigate to="tenants" replace />} />
          <Route path="tenants" element={<PlatformAdminTenants />} />
          <Route path="users" element={<PlatformAdminUsers />} />
        </Route>

        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/campanhas" element={<Campanhas />} />
          <Route path="/influenciadores" element={<Influenciadores />} />
          <Route path="/cronograma" element={<Cronograma />} />
          <Route path="/relatorios" element={<Relatórios />} />
          <Route path="/performance" element={<PainelPerformance />} />
          <Route path="/entrega/:id" element={<DetalheEntrega />} />
          <Route path="/equipe" element={<Equipe />} />
          <Route path="/configuracoes" element={<Configurações />} />
          <Route path="/configuracoes/agencia" element={<ConfiguraçõesAgência />} />
          <Route path="/aprovacoes" element={<Aprovações />} />
          <Route path="/pagamentos" element={<Pagamentos />} />
          <Route path="/clientes" element={<Clientes />} />
          <Route path="/curadoria" element={<Curadoria />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

/** Rotas quando o hostname é admin.runnaflow.com (rewrite já mapeou, mas
    precisamos renderizar direto pro admin layout sem CampaignProvider). */
function AdminHostRoutes() {
  return (
    <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={<PlatformAdminGate><PlatformAdminLayout /></PlatformAdminGate>}
        >
          <Route index element={<Navigate to="tenants" replace />} />
          <Route path="tenants" element={<PlatformAdminTenants />} />
          <Route path="users" element={<PlatformAdminUsers />} />
          <Route path="platform-admin" element={<Navigate to="/tenants" replace />} />
          <Route path="platform-admin/tenants" element={<Navigate to="/tenants" replace />} />
          <Route path="platform-admin/users" element={<Navigate to="/users" replace />} />
        </Route>
      </Routes>
    </Suspense>
  )
}

export default function App() {
  const adminHost = isAdminHost()

  return (
    <BrowserRouter>
      <ThemeProvider>
        <ErrorBoundary>
          <AuthProvider>
            <EnvBadge />
            <CookieBanner />
            {adminHost ? (
              <>
                <AdminHostRoutes />
                <Toaster
                  theme="dark"
                  position="bottom-right"
                  toastOptions={{
                    style: {
                      background: 'var(--color-card)',
                      border: '1px solid var(--color-card-border)',
                      color: 'var(--color-bright)',
                      fontFamily: 'var(--font-sans)',
                    },
                  }}
                />
              </>
            ) : (
              <ClientProvider>
                <CampaignProvider>
                  <AppRoutes />
                  <Toaster
                    theme="dark"
                    position="bottom-right"
                    toastOptions={{
                      style: {
                        background: 'var(--color-card)',
                        border: '1px solid var(--color-card-border)',
                        color: 'var(--color-bright)',
                        fontFamily: 'var(--font-sans)',
                      },
                    }}
                  />
                </CampaignProvider>
              </ClientProvider>
            )}
          </AuthProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </BrowserRouter>
  )
}
